import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { DateFormatPipe } from '@/extensions/datePipe';
import { ReviewResumePipe } from '@/extensions/review-resume.pipe';
import { ToNumberPipe } from '@/extensions/to-number.pipe';
import { PropertyHelper } from '@/helpers/property.helper';
import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { PropertyVisitFeedback, RealProperty, Sharing, SharingService } from '@eemo/api-client';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { PropertyWorkDialogComponent } from '../property-work-dialog/property-work-dialog.component';
import { InputComponent } from '../ui-forms/input/input.component';

export class VisitFeedbackDialogData {
  prop: RealProperty;
  sharingUid: string;
  visitFeedback?: PropertyVisitFeedback;
}

interface Review {
  name: string;
  formControlName: string;
}

@Component({
  selector: 'app-visit-feedback-dialog',
  standalone: true,
  templateUrl: './visit-feedback-dialog.component.html',
  styleUrl: './visit-feedback-dialog.component.scss',
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    InputComponent,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    NgIf,
    DateFormatPipe,
    ToNumberPipe,
    ReviewResumePipe,
    MatIconModule,
    TooltipDirective,
    TranslateModule,
    MatProgressSpinnerModule,
    MatButtonModule
  ]
})
export class VisitFeedbackDialogComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  dialogTitle: string;
  formSubmitting = false;

  sharing: Sharing;
  propAddress: string;
  sharingTargetUser: string;
  feedbackUser: string;

  subGet: Subscription = new Subscription();
  subCreate: Subscription = new Subscription();

  readonly NB_NOTE = new Array(5);

  reviews: Review[] = [
    { name: 'Situation géographique', formControlName: 'location' },
    { name: 'Agencement intérieur', formControlName: 'inside' },
    { name: 'Agencement extérieur', formControlName: 'outside' },
    { name: "Gestion de l'énergie", formControlName: 'energy' },
    { name: 'Appréciation globale', formControlName: 'global' }
  ];

  get form_visitDate() {
    return this.form.get('visitDate') as FormControl<Date>;
  }

  get form_review() {
    return this.form.get('review') as FormGroup;
  }

  get form_pros() {
    return this.form.get('pros') as FormControl<string>;
  }

  get form_cons() {
    return this.form.get('cons') as FormControl<string>;
  }

  get form_estimate() {
    return this.form.get('estimate') as FormControl<string>;
  }

  get form_others() {
    return this.form.get('others') as FormControl<string>;
  }

  get form_comments() {
    return this.form.get('comments') as FormControl<string>;
  }

  get form_goFurther() {
    return this.form.get('goFurther') as FormControl<boolean>;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: VisitFeedbackDialogData,
    private sharingService: SharingService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    public propertyHelper: PropertyHelper,
    public dialogRef: MatDialogRef<PropertyWorkDialogComponent>,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.dialogTitle = 'Rapport de visite';
  }

  ngOnInit(): void {
    this.subGet = this.sharingService.getSharing(this.data.sharingUid).subscribe((s) => {
      this.sharing = s;
      this.sharingTargetUser = UserHelper.userSummmaryDisplayName(s.targetUser);
    });
    this.propAddress = this.propertyHelper.getAddressSummary(this.data.prop.address);
    this.form = this.fb.group({
      visitDate: [null, Validators.required],
      review: this.fb.group({
        location: [null, Validators.required],
        inside: [null, Validators.required],
        outside: [null, Validators.required],
        energy: [null, Validators.required],
        global: [null, Validators.required]
      }),
      pros: [null, Validators.required],
      cons: [null, Validators.required],
      estimate: [null],
      others: [null],
      comments: [null],
      goFurther: [null, Validators.required]
    });

    if (this.data.visitFeedback) {
      this.form.patchValue(this.data.visitFeedback);
      this.form.disable();
      this.feedbackUser = UserHelper.userSummmaryDisplayName(this.data.visitFeedback.user);
    }
  }

  ngOnDestroy(): void {
    this.subGet.unsubscribe();
    this.subCreate.unsubscribe();
  }

  onSubmit() {
    if (this.form.valid) {
      this.formSubmitting = true;

      this.subCreate = this.sharingService.addSharingVisitFeedback(this.data.sharingUid, this.form.value).subscribe({
        next: () => {
          this.dialogRef.close(true);
          this.alertService.success('Rapport de visite enregistré');
        },
        error: () => {
          this.dialogRef.close(false);
        }
      });
    }
  }

  animate(e: PointerEvent) {
    if (!this.form_pros.disabled) {
      (e.target as HTMLElement)?.classList.add('scale-animation');
    }
  }

  resetAnimation(e: AnimationEvent) {
    (e.target as HTMLElement)?.classList.remove('scale-animation');
  }
}
